import React from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import { hideDialog } from '../../actions/dialog';
import $ from 'jquery';
import Logger from '../../util/Logger';
import './Dialog.css';

/**
 * Dialog class in JSX
 *
 * This class is not meant to be accessed directly but rather via Redux events showDialog() and hideDialog().
 *
 * the showDialog() function takes an argument payload which has the following format:
 *
 * title: string = The string to display as the Dialog title. Omitting this argument will hide the title
 *                 section entirely.
 *
 * body: jsx = The body of the Dialog defined as JSX. Omitting this argument will throw an error.
 *
 * ok: string = The text to display on the positive dialog button. Omitting this argument will hide the
 *              positive button entirely
 *
 * cancel: string = The text to display on the negative dialog button. Omitting this argument will hide the
 *                  negative button entirely
 *
 * extraActions: Array<JSX> = An array of extra buttons which will display to the left of the standard buttons.
 *
 * onOk: function () -> boolean = Optional callback function which runs on the ok button press.
 *                                If this function does not exist, the dialog will simply close.
 *                                If this function exists and returns truthy the dialog will not close
 *                                automatically and it is up to the caller to handle the dialog.
 *
 * onCancel: function () -> boolean = Optional callback function which runs on the cancel button press.
 *                                If this function does not exist, the dialog will simply close.
 *                                If this function exists and returns truthy the dialog will not close
 *                                automatically and it is up to the caller to handle the dialog.
 *
 * onHidden: function () = Optional callback function which runs when the dialog is hidden.
 *
 * closeOnEscape: boolean = Optional flag which decides if the modal should close on Escape key press
 *                          or not. Omitting it will have the modal close on Esc by default.
 *
 * onCloseOnClickOutside: function () -> boolean = Optional callback function which runs on when Dialog closes from escape key press.
 *
 * closeOnClickOutside: boolean = Optional flag which decides if the modal should close on Click outside
 *                          or not. Omitting it will have the modal close on click outside by default.
 *
 * onCloseOnClickOutside: function () -> boolean = Optional callback function which runs on when Dialog closes from click outside.
 *
 * autoFocus: boolean = Optional flag - ONLY valid for BaseDialog implementations, on by default for Redux dialog.
 *                                      if present will automatically focus the Dialog
 *
 * customModalDialogClass: string = Optional flag - Optional classes to be applied to the modal-dialog div
 *                                                  in addition to the standard dialog classes.
 *
 * customModalBodyClass: string = Optional flag - Optional classes to be applied to the modal-body div
 *                                                  in addition to the standard dialog classes.
 */
class Dialog extends React.Component {

  setDivElementRef = (element) => {
    this.divElementRef = element;
  };
  setDialogRef = (element) => {
    this.dialogRef = element;
  };
  handlePositiveButtonClicked = ($event) => {
    $event.preventDefault();

    const { onOk } = this.props.options || this.props;
    if (onOk) {
      if (!onOk()) {
        this.hideModal();
      }
    } else {
      this.hideModal();
    }
  };
  handleNegativeButtonClicked = ($event) => {
    $event.preventDefault();

    const { onCancel } = this.props.options || this.props;
    if (onCancel) {
      if (!onCancel()) {
        this.hideModal();
      }
    } else {
      this.hideModal();
    }
  };
  handleKeyPress = ($event) => {
    if ($event.key === 'Escape' || $event.code === 13) {
      Logger.info('Escape key pressed');
      const { closeOnEscape, onCloseOnEscape } = this.props.options || this.props;
      // We do this weird check to show that you must pass `closeOnEscape: false` explicitly
      // to stop dialogs from closing with the escape key
      if (!(closeOnEscape === false)) {
        if (onCloseOnEscape) {
          onCloseOnEscape();
        }
        this.hideModal();
      }
    }
  };
  handleClick = ($event) => {
    $event.preventDefault();
    if (this.dialogRef && !this.dialogRef.contains($event.target)) {
      Logger.info('Click outside caught');
      const { closeOnClickOutside, onCloseOnClickOutside } = this.props.options || this.props;
      // We do this weird check to show that you must pass `closeOnEscape: false` explicitly
      // to stop dialogs from closing with the escape key
      if (!(closeOnClickOutside === false)) {
        if (onCloseOnClickOutside) {
          onCloseOnClickOutside();
        }
        this.hideModal();
      }
    }
  };
  hideModal = () => {
    const { hideDialog, options } = this.props;
    const { onHidden } = options || this.props;
    if (hideDialog) {
      Logger.info('Hiding modal');
      if (onHidden) {
        onHidden();
      }
      hideDialog();
    }
  };

  constructor (props) {
    super(props);

    const { hideDialog } = this.props;
    if (!hideDialog) {
      Logger.assert('Missing hideDialog(). Must either be created via Redux or provide prop');
    }
  }

  componentDidMount () {
    const dialog = $('#dialog');
    if (dialog) {
      // On the bootstrap hide event, we change the state
      dialog.on('hide.bs.modal', $event => {
        $event.preventDefault();
        this.hideModal();
      });
    }
  }

  componentDidUpdate (prevProps) {
    const { show, options } = this.props;
    if (show && show !== prevProps.show && (options || this.props.autoFocus)) {
      // Force the focus onto the shown dialog so that we can clear the focus later.
      setTimeout(() => {
        if (this.divElementRef) {
          if (this.divElementRef.focus) {
            this.divElementRef.focus();
          }
        }
      }, 100);
    }
  }

  render () {
    const { show, options } = this.props;
    if (show) {
      // If options is undefined - we are calling this directly from a BaseDialog
      const { title, body, ok, cancel, extraActions } = options || this.props;
      const { customModalDialogClass, customModalBodyClass } = options || this.props;

      const displayTitleIfProvided = title && (
        <div className="modal-header">
          <h5 className="modal-title">{ title }</h5>
        </div>
      );

      const displayOkButtonIfProvided = ok && (
        <button
          type="button"
          className="btn btn-outline pop-dialog-btn font-weight-semibold"
          onClick={ this.handlePositiveButtonClicked }>
          { ok }
        </button>
      );

      const displayCancelButtonIfProvided = cancel && (
        <button
          type="button"
          className="btn btn-outline font-weight-semibold"
          onClick={ this.handleNegativeButtonClicked }>
          { cancel }
        </button>
      );

      if (!body) {
        Logger.assert('Dialog body must be provided as valid JSX');
      }

      const isAppLevel = !!this.props.options;

      // Set the display as block so that the modal always displays if the component renders
      return (
        <div
          ref={ this.setDivElementRef }
          id="dialog"
          className={ `modal modal-backdrop d-block ${isAppLevel ? 'above-all' : ''}` }
          onKeyUp={ this.handleKeyPress }
          onClick={ this.handleClick }
          tabIndex="-1"
          role="dialog">
          <div
            role="document"
            ref={ this.setDialogRef }
            className={ `modal-dialog modal-dialog-centered
            ${!!customModalDialogClass && ' ' + customModalDialogClass}` }>
            <div className="modal-content">
              { displayTitleIfProvided }
              <div
                className={ `modal-body ${!!customModalBodyClass && ' ' + customModalBodyClass}` }>
                { body }
              </div>
              <div className="modal-footer">
                { (!!extraActions && extraActions.length > 0) && extraActions.map(e => e) }
                { displayCancelButtonIfProvided }
                { displayOkButtonIfProvided }
              </div>
            </div>
          </div>
        </div>
      );
    } else {
      return null;
    }
  }

}

const mapStateToProps = state => {
  return {
    show: state.dialog.show,
    options: state.dialog.options || {}
  };
};

export default connect(mapStateToProps, { hideDialog })(Dialog);

const renderSubtreeIntoContainer = ReactDOM.unstable_renderSubtreeIntoContainer;

export class BaseDialog extends React.Component {

  _target = null;
  _component = null;

  update = () => {
    const {
      show,
      title,
      body,
      ok,
      cancel,
      extraActions,
      closeOnEscape,
      closeOnClickOutside
    } = this.props;
    const {
      onOk,
      onCancel,
      onHidden,
      onCloseOnClickOutside,
      onCloseOnEscape,
      hideDialog
    } = this.props;
    const {
      customModalDialogClass,
      customModalBodyClass
    } = this.props;

    this._component = renderSubtreeIntoContainer(this, <Dialog
      show={ show }
      title={ title }
      body={ body }
      ok={ ok }
      cancel={ cancel }
      onOk={ onOk }
      onCancel={ onCancel }
      onHidden={ onHidden }
      extraActions={ extraActions }
      customModalDialogClass={ customModalDialogClass }
      customModalBodyClass={ customModalBodyClass }
      closeOnEscape={ closeOnEscape }
      closeOnClickOutside={ closeOnClickOutside }
      onCloseOnClickOutside={ onCloseOnClickOutside }
      onCloseOnEscape={ onCloseOnEscape }
      hideDialog={ () => {
        ReactDOM.unmountComponentAtNode(this._target);
        hideDialog();
      } }
    />, this._target);
  };

  componentDidMount () {
    this._target = document.body.appendChild(document.createElement('div'));
    this.update();
  }

  componentDidUpdate () {
    this.update();
  }

  componentWillUnmount () {
    ReactDOM.unmountComponentAtNode(this._target);
    document.body.removeChild(this._target);

    this._component = null;
    this._target = null;
  }

  render () {
    return null;
  }
}
